import { usePublicMetadata } from './seo/PublicMetadata.jsx';
import { PublicBootstrapProvider, usePublicHostname } from './seo/PublicBootstrap.jsx';
import { FeedbackProvider, FeedbackToaster } from '@mts/ui/feedback';
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { isPlatformHostname } from './services/api';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import { PageShell } from './components/layout/PageShell';
import PageViewTracker from './components/analytics/PageViewTracker';
import HomePage from './pages/HomePage';
import ComingSoonPage from './pages/ComingSoonPage';
import DirectoryPage from './pages/DirectoryPage';
import SingleListingNewPage from './pages/SingleListingNewPage';
import ListingClaimPage from './pages/ListingClaimPage';
import ClaimAutomationPage from './pages/ClaimAutomationPage';
import ManualListingClaimPage from './pages/ManualListingClaimPage';
import ClaimNewPage from './pages/ClaimNewPage';
import DealsPage from './pages/DealsPage';
import OfferDetailPage from './pages/OfferDetailPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ExpertsPage from './pages/ExpertsPage';
import ExpertDetailPage from './pages/ExpertDetailPage';
import ExpertContributionPage from './pages/ExpertContributionPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import DirectoryPricingPage from './pages/DirectoryPricingPage';
import SharedMailerLandingPage from './features/mailers/public/SharedMailerLandingPage.jsx';
import MailerTrackingPage from './features/mailers/public/MailerTrackingPage.jsx';
import SpotlightDealPage from './features/mailers/public/SpotlightDealPage.jsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import PublicInvoicePage from './pages/PublicInvoicePage';
import PublicSalesMaterialPage from './pages/PublicSalesMaterialPage';
import RedeemPromotionPage from './pages/RedeemPromotionPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import NotFoundPage from './pages/NotFoundPage';
import AccessDeniedPage from './pages/AccessDeniedPage.jsx';
import ProfilePage from './pages/ProfilePage';
import { usePublicTenant } from './hooks/usePublicTenant';
import { updateDocumentFavicon } from './utils/faviconSettings';
import { initializeCookieConsent } from './services/cookieConsentConfig';
import { authClient } from './lib/authClient';
import { identifyPostHogUser } from './lib/posthog';
import { linkVisitorIdentityToCurrentUser } from './services/visitorTracking';
import { publicRoutes } from './lib/routes';
import PreviewPage from './pages/PreviewPage.tsx';
import { CommunityProgramLandingPage, CommunityProgramBallotPage, CommunityProgramCategoryPage, DirectNomineeVotePage } from './pages/CommunityProgramPage.jsx';
import { canonicalRedirectUrl } from './utils/canonicalUrls.js';
import { buildRouteMetadata, removePublicMetadata } from './utils/publicMetadata.js';

function hexToChannels(hex) {
  const match = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return match
    ? `${parseInt(match[1], 16)} ${parseInt(match[2], 16)} ${parseInt(match[3], 16)}`
    : null;
}

function CSSVariableInjector() {
  const { brandTheme } = usePublicTenant();

  useEffect(() => {
    if (!brandTheme) return;

    const root = document.documentElement;
    const vars = {
      '--primary': brandTheme.primary,
      '--primary-hover': brandTheme.primaryHover,
      '--primary-soft': brandTheme.primarySoft,
      '--primary-foreground': brandTheme.primaryForeground,
      '--secondary': brandTheme.secondary,
      '--secondary-hover': brandTheme.secondaryHover,
      '--secondary-soft': brandTheme.secondarySoft,
      '--secondary-foreground': brandTheme.secondaryForeground,
      '--spotlight': brandTheme.spotlight,
      '--spotlight-soft': brandTheme.spotlightSoft,
      '--spotlight-foreground': brandTheme.spotlightForeground,
      '--hero-heading-color': brandTheme.text,
      '--hero-body-color': brandTheme.muted,
      '--hero-overlay-color': brandTheme.surface,
      '--background': brandTheme.background,
      '--surface': brandTheme.surface,
      '--card': brandTheme.card,
      '--border': brandTheme.border,
      '--text': brandTheme.text,
      '--muted': brandTheme.muted,
      '--ui-card-border': brandTheme.border,
      '--button-primary-bg': brandTheme.primary,
      '--button-primary-text': brandTheme.primaryForeground,
      '--button-primary-hover': brandTheme.primaryHover,
      '--button-secondary-bg': brandTheme.surface,
      '--button-secondary-text': brandTheme.primary,
      '--button-secondary-border': brandTheme.primarySoft,
      '--button-ghost-text': brandTheme.primary,
      '--badge-basic-bg': brandTheme.primarySoft,
      '--badge-basic-text': brandTheme.primary,
      '--badge-featured-bg': brandTheme.primary,
      '--badge-featured-text': brandTheme.primaryForeground,
      '--badge-spotlight-bg': brandTheme.spotlight,
      '--badge-spotlight-text': brandTheme.spotlightForeground,
      '--badge-basic-color': brandTheme.primarySoft,
      '--badge-basic-text-color': brandTheme.primary,
      '--badge-featured-color': brandTheme.primary,
      '--badge-featured-text-color': brandTheme.primaryForeground,
      '--badge-spotlight-color': brandTheme.spotlight,
      '--badge-spotlight-text-color': brandTheme.spotlightForeground,
      '--success': brandTheme.success,
      '--success-foreground': brandTheme.successForeground,
      '--warning': brandTheme.warning,
      '--warning-foreground': brandTheme.warningForeground,
      '--danger': brandTheme.danger,
      '--danger-foreground': brandTheme.dangerForeground,
      '--primary-rgb': hexToChannels(brandTheme.primary),
      '--primary-hover-rgb': hexToChannels(brandTheme.primaryHover),
      '--primary-foreground-rgb': hexToChannels(brandTheme.primaryForeground),
      '--secondary-rgb': hexToChannels(brandTheme.secondary),
      '--secondary-hover-rgb': hexToChannels(brandTheme.secondaryHover),
      '--secondary-foreground-rgb': hexToChannels(brandTheme.secondaryForeground),
      '--spotlight-rgb': hexToChannels(brandTheme.spotlight),
      '--spotlight-hover-rgb': hexToChannels(brandTheme.spotlightHover),
      '--spotlight-foreground-rgb': hexToChannels(brandTheme.spotlightForeground),
      '--background-rgb': hexToChannels(brandTheme.background),
      '--surface-rgb': hexToChannels(brandTheme.surface),
      '--surface-alt-rgb': hexToChannels(brandTheme.surface),
      '--border-rgb': hexToChannels(brandTheme.border),
      '--text-rgb': hexToChannels(brandTheme.text),
      '--body-rgb': hexToChannels(brandTheme.text),
      '--muted-rgb': hexToChannels(brandTheme.muted),
      '--success-rgb': hexToChannels(brandTheme.success),
      '--warning-rgb': hexToChannels(brandTheme.warning),
      '--danger-rgb': hexToChannels(brandTheme.danger),
    };

    Object.entries(vars).forEach(([name, value]) => {
      if (value) root.style.setProperty(name, value);
    });
  }, [brandTheme]);

  return null;
}

function FaviconUpdater() {
  const { tenantFaviconUrl } = usePublicTenant();

  useEffect(() => {
    updateDocumentFavicon(tenantFaviconUrl);
  }, [tenantFaviconUrl]);

  return null;
}

function AuthIdentityBridge() {
  const { data: session } = authClient.useSession();

  useEffect(() => {
    const user = session?.user;
    if (!user?.id) {
      return;
    }

    let cancelled = false;

    Promise.all([linkVisitorIdentityToCurrentUser()])
      .then(([linkResult]) => {
        if (cancelled || !linkResult?.linked) {
          return;
        }

        identifyPostHogUser({
          id: user.id,
          email: user.email,
          name: user.name,
          displayName: linkResult?.userDisplayName || linkResult?.visitorDisplayName || user.name,
          visitorId: linkResult?.visitorId,
        });
      })
      .catch(() => {
        // Keep the PostHog identity anonymous unless the database visitor link
        // succeeds. A later session refresh will retry the bridge.
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, session?.user?.email, session?.user?.name]);

  return null;
}

function PublicMetadataManager() {
  const location = useLocation();
  const { tenant, loading, spotlightName, tenantLogoConfig, tenantLogoImageUrl, websiteStatus } = usePublicTenant();

  useEffect(() => {
    removePublicMetadata();
    if (loading || !tenant) return;
    const redirectUrl = canonicalRedirectUrl(tenant, window.location);
    if (redirectUrl) {
      window.location.replace(redirectUrl);
      return;
    }
  }, [loading, tenant, location.pathname, location.search]);

  usePublicMetadata(() => {
    if (loading || !tenant) return;
    const metadata = buildRouteMetadata({
      tenant,
      pathname: location.pathname,
      search: location.search,
      siteName: spotlightName,
      cityName: tenantLogoConfig?.cityName,
      logoUrl: tenantLogoImageUrl,
    });
    if (websiteStatus === 'maintenance') {
      metadata.canonicalUrl = '';
      metadata.robots = 'noindex,nofollow';
    }
    return metadata;
  }, [loading, location.pathname, location.search, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl, websiteStatus]);

  return null;
}

function MaintenancePlaceholder({ tenant }) {
  const siteName = tenant?.name || 'This Spotlight';

  return (
    <div className="min-h-screen bg-[var(--background,#f8fafc)] px-6 py-16 text-[var(--text,#0f172a)]">
      <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center text-center">
        <div className="rounded-full border border-[var(--border,#e2e8f0)] bg-[var(--surface,#ffffff)] px-4 py-2 text-sm font-semibold text-[var(--muted,#64748b)] shadow-sm">
          Maintenance Mode
        </div>
        <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
          {siteName} is getting a quick update.
        </h1>
        <p className="mt-4 max-w-xl text-base leading-7 text-[var(--muted,#64748b)]">
          The public website is temporarily unavailable while the tenant updates their site. Please check back soon.
        </p>
      </div>
    </div>
  );
}

function TenantGate({ children }) {
  const { tenant, loading, error, websiteStatus } = usePublicTenant();
  const hostname = usePublicHostname();
    const isPlatformHost = isPlatformHostname(hostname);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100 text-base-content/70">
        Loading...
      </div>
    );
  }

  if (!isPlatformHost && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100 px-6">
        <div className="max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-base-content">Tenant service temporarily unavailable</h1>
          <p className="mt-4 break-words text-base-content/70">{error.message || String(error)}</p>
          <button
            type="button"
            className="mt-6 rounded-lg bg-primary px-5 py-2.5 font-semibold text-primary-content"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

    // Allow platform hosts such as mts.homelab and mytownspotlight.dev to render without a tenant.
    if (!isPlatformHost && !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100 px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-base-content">This tenant not found</h1>
        </div>
      </div>
    );
  }

  if (!isPlatformHost && tenant && websiteStatus === 'maintenance') {
    return (
      <>
        <CSSVariableInjector />
        <MaintenancePlaceholder tenant={tenant} />
      </>
    );
  }

  return children;
}


function RedirectWithSearch({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search || ''}${location.hash || ''}`} replace />;
}

function BusinessListingRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  const encodedSlug = encodeURIComponent(slug || '');
  return <Navigate to={`/listings/${encodedSlug}${location.search || ''}${location.hash || ''}`} replace />;
}

function ManagePortalListingRedirect() {
  const { listingId, '*': splat } = useParams();
  const location = useLocation();
  const encodedListingId = encodeURIComponent(listingId || '');
  const suffix = splat ? `/${splat}` : '';
  return <Navigate to={`/manage/listings/${encodedListingId}${suffix}${location.search || ''}${location.hash || ''}`} replace />;
}

const COMING_SOON_HOSTNAMES = new Set([
  'mytownspotlight.com',
  'www.mytownspotlight.com',
  'mts.homelab',
]);

function AppRoutes() {
  const location = useLocation();
  const hostname = usePublicHostname().toLowerCase().replace(/\.$/, '');
  const showComingSoon = location.pathname === '/' && COMING_SOON_HOSTNAMES.has(hostname);

  // Platform-level preview: intentionally bypasses tenant resolution, branding, and navigation.
  if (location.pathname === '/preview') {
    return <PreviewPage />;
  }

  if (showComingSoon) {
    return <ComingSoonPage />;
  }

  return (
    <TenantGate>
      <CSSVariableInjector />
      <FaviconUpdater />
      <PageShell>
        <Routes>
          <Route path={publicRoutes.home()} element={<HomePage />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/directory" element={<RedirectWithSearch to={publicRoutes.listings()} />} />
          <Route path="/business/:slug" element={<BusinessListingRedirect />} />
          <Route path={publicRoutes.listings()} element={<DirectoryPage />} />
          <Route path="/listings/:categorySlug/:slug/claim/auto-approve" element={<ClaimAutomationPage mode="auto" />} />
          <Route path="/listings/:categorySlug/:slug/claim/phone" element={<ClaimAutomationPage mode="phone" />} />
          <Route path="/listings/:slug/claim/auto-approve" element={<ClaimAutomationPage mode="auto" />} />
          <Route path="/listings/:slug/claim/phone" element={<ClaimAutomationPage mode="phone" />} />
          <Route path="/listings/:categorySlug/:slug/claim" element={<ListingClaimPage />} />
          <Route path="/listings/:slug/claim" element={<ListingClaimPage />} />
          <Route path="/claim/:slug" element={<ListingClaimPage />} />
          <Route path="/listings/:categorySlug/:slug" element={<SingleListingNewPage />} />
          <Route path={publicRoutes.listing()} element={<SingleListingNewPage />} />
          <Route path="/claim/new" element={<ClaimNewPage />} />
          <Route path="/claim/new/manual" element={<ManualListingClaimPage />} />
          <Route path="/add-listing" element={<ClaimNewPage />} />
          <Route path="/redeem/:code" element={<RedeemPromotionPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/:section" element={<ProfilePage />} />
          <Route path="/dashboard" element={<Navigate to="/manage" replace />} />
          <Route path="/portal" element={<Navigate to="/manage" replace />} />
          <Route path="/portal/listings/:listingId/*" element={<ManagePortalListingRedirect />} />
          <Route path="/admin" element={<Navigate to="/manage" replace />} />
          <Route path="/deals" element={<RedirectWithSearch to={publicRoutes.offers()} />} />
          <Route path={publicRoutes.offers()} element={<DealsPage />} />
          <Route path="/offers/category/:categorySlug" element={<DealsPage />} />
          <Route path="/offers/:slug" element={<OfferDetailPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId" element={<EventDetailPage />} />
          <Route path="/community-programs/:slug" element={<CommunityProgramLandingPage />} />
          <Route path="/community-programs/:slug/ballot" element={<CommunityProgramBallotPage />} />
          <Route path="/community-programs/:slug/category/:categoryId" element={<CommunityProgramCategoryPage />} />
          <Route path="/vote/:nomineeId" element={<DirectNomineeVotePage />} />
          <Route path="/articles" element={<RedirectWithSearch to={publicRoutes.blog()} />} />
          <Route path={publicRoutes.blog()} element={<BlogListPage />} />
          <Route path="/blog/category/:slug" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route path="/experts" element={<ExpertsPage />} />
          <Route path="/experts/:slug" element={<ExpertDetailPage />} />
          <Route path="/contribute/expert/:token" element={<ExpertContributionPage />} />
          <Route path={publicRoutes.about()} element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/advertise/spotlight" element={<DirectoryPricingPage />} />
          <Route path="/pricing" element={<Navigate to="/advertise/spotlight" replace />} />
          <Route path="/advertise/mailer" element={<SharedMailerLandingPage />} />
          <Route path="/advertise/mailer/tracking" element={<MailerTrackingPage />} />
          <Route path="/advertise/mailer/spotlight-deal" element={<SpotlightDealPage />} />
          <Route path="/mailer/availability" element={<SharedMailerLandingPage availabilityOnly />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/pay/:invoiceToken" element={<PublicInvoicePage />} />
          <Route path="/files/:materialToken/:slug" element={<PublicSalesMaterialPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </PageShell>
    </TenantGate>
  );
}

function AppFeedback({children}) {
  const {data:session} = authClient.useSession();
  const {tenant} = usePublicTenant();
  return <FeedbackProvider scope={`${session?.user?.id || 'anonymous'}:${tenant?.id || ''}`}><FeedbackToaster/>{children}</FeedbackProvider>;
}

export function AppContent() {
  // Initialize cookie consent banner on app load
  useEffect(() => {
    initializeCookieConsent();
  }, []);

  return (
      <AppFeedback>
      <PageViewTracker />
      <AuthIdentityBridge />
      <PublicMetadataManager />
      <AppRoutes />
    </AppFeedback>
  );
}

export default function App({ bootstrap = null }) {
  return <BrowserRouter><PublicBootstrapProvider value={bootstrap}><AppContent /></PublicBootstrapProvider></BrowserRouter>;
}
